// The API client (ADR 0013): openapi-fetch over the generated types, plus the behaviour
// every request needs. Same-origin cookies; X-CSRF-Token on unsafe methods; problem+json
// errors thrown as a typed ApiError; a 401 sends the browser to /login.
import createClient, { type Client, type Middleware } from 'openapi-fetch';
import type { components, paths } from './schema.gen';

export type Problem = components['schemas']['Problem'];
export type ProblemCode = Problem['code'];

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** An error response from the API. Switch on `code`, never on `status` or `message`. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ProblemCode;
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.status = problem.status;
    this.code = problem.code;
    this.problem = problem;
  }
}

function isProblem(value: unknown): value is Problem {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Problem).code === 'string' &&
    typeof (value as Problem).status === 'number'
  );
}

/** Turns any error body into a Problem, so callers always get an ApiError. */
function toProblem(body: unknown, response: Response): Problem {
  if (isProblem(body)) return body;
  return {
    type: 'about:blank',
    title: response.statusText || 'Request failed',
    status: response.status,
    code: response.status >= 500 ? 'internal' : 'bad_request',
  };
}

export interface ApiClientOptions {
  /** Defaults to this page's origin; the API is always same-origin (ADR 0014). */
  baseUrl?: string;
  /** Called on a 401. Defaults to sending the browser to /login. */
  onUnauthenticated?: () => void;
  /**
   * An admin's client for viewing as this user: every request carries X-View-As (ADR
   * 0017's A19 amendment). One per viewed user, with its own query cache (api/viewAs.ts).
   */
  viewAs?: string;
}

export interface ApiClient extends Client<paths> {
  /** Stores the session's CSRF token, from GET /api/me, for unsafe requests. */
  setCsrfToken(token: string | null): void;
}

function goToLogin() {
  if (window.location.pathname !== '/login') window.location.assign('/login');
}

export function createApiClient({
  baseUrl = window.location.origin,
  onUnauthenticated = goToLogin,
  viewAs,
}: ApiClientOptions = {}): ApiClient {
  let csrfToken: string | null = null;

  const behaviour: Middleware = {
    onRequest({ request }) {
      if (UNSAFE_METHODS.has(request.method) && csrfToken) {
        request.headers.set('X-CSRF-Token', csrfToken);
      }
      if (viewAs) request.headers.set('X-View-As', viewAs);
      return request;
    },
    onResponse({ response }) {
      if (response.status === 401) onUnauthenticated();
      return response;
    },
  };

  const client = createClient<paths>({ baseUrl, credentials: 'same-origin' });
  client.use(behaviour);
  return Object.assign(client, {
    setCsrfToken(token: string | null) {
      csrfToken = token;
    },
  });
}

/**
 * Returns the response's data, or throws an ApiError built from its problem+json body.
 * Use it in every query and mutation function:
 *
 *   const me = await unwrap(api.GET('/api/me'));
 */
export async function unwrap<T>(
  request: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await request;
  if (!response.ok) throw new ApiError(toProblem(error, response));
  return data as T;
}
